import React, { useState, Fragment, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import API from "../../services/API";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

const THEME = {
  navy: "#062E63",
  blue: "#0B4DA2",
  blue2: "#2563EB",
  sky: "#EAF2FF",
  border: "#DDE7F3",
  text: "#0F172A",
  muted: "#64748B",
  danger: "#DC2626",
};

const AnnouncementPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentMap, setDepartmentMap] = useState({});

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [newAnnouncementTitle, setNewAnnouncementTitle] = useState("");
  const [newAnnouncementBody, setNewAnnouncementBody] = useState("");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [newAnnouncementImage, setNewAnnouncementImage] = useState(null);
  const [newAnnouncementImagePreview, setNewAnnouncementImagePreview] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  const getUploadHeaders = () => ({
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  useEffect(() => {
    getDepartments();
  }, []);

  useEffect(() => {
    if (Object.keys(departmentMap).length > 0 || !isLoading) {
      fetchAnnouncements();
    }
  }, [departmentMap, isLoading]);

  const getDepartments = () => {
    const headers = getHeaders();

    API.get("/portal/getClasses", { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const data = response.data.data || [];

          const allDepartmentsOption = {
            label: "Semua Jabatan",
            value: "all",
          };

          const mapping = {};
          const uniqueDepartmentMap = new Map();

          data.forEach((item) => {
            const departmentId = item.school_id;
            const departmentName =
              item.department_name ||
              item.class_name ||
              item.school_name ||
              `Jabatan ${departmentId}`;

            if (!departmentId) return;

            if (!uniqueDepartmentMap.has(departmentId)) {
              uniqueDepartmentMap.set(departmentId, {
                label: departmentName,
                value: departmentId,
              });
            }

            mapping[departmentId] = departmentName;
          });

          const formattedDepartments = Array.from(uniqueDepartmentMap.values());

          setDepartments([allDepartmentsOption, ...formattedDepartments]);
          setSelectedDepartment([allDepartmentsOption]);
          setDepartmentMap(mapping);
        } else {
          toast.error(response.data.message || "Gagal mendapatkan senarai jabatan", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Ralat semasa mendapatkan senarai jabatan", { autoClose: 5000 });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchAnnouncements = () => {
    const headers = getHeaders();

    API.get("/portal/obtainBulletinAdmin", { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const uniqueAnnouncementsMap = new Map();

          (response.data.data || []).forEach((announcement) => {
            const uniqueKey = `${announcement.title}_${announcement.message}`;
            const departmentId = announcement.school_id;
            const departmentName =
              departmentMap[departmentId] ||
              (departmentId === "all" ? "Semua Jabatan" : "Jabatan Tidak Dikenal Pasti");

            if (!uniqueAnnouncementsMap.has(uniqueKey)) {
              uniqueAnnouncementsMap.set(uniqueKey, {
                ...announcement,
                departments: [
                  {
                    id: departmentId,
                    name: departmentName,
                  },
                ],
              });
            } else {
              const existingDepartments =
                uniqueAnnouncementsMap.get(uniqueKey).departments || [];

              if (!existingDepartments.some((d) => d.id === departmentId)) {
                existingDepartments.push({
                  id: departmentId,
                  name: departmentName,
                });
              }
            }
          });

          setAnnouncements(Array.from(uniqueAnnouncementsMap.values()));
        } else {
          toast.error(response.data.message || "Gagal mendapatkan pengumuman", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Ralat semasa mendapatkan pengumuman", { autoClose: 5000 });
      });
  };

  const openAddModal = () => {
    setNewAnnouncementTitle("");
    setNewAnnouncementBody("");
    setNewAnnouncementImage(null);
    setNewAnnouncementImagePreview("");
    setSelectedDepartment(
      departments.length > 0
        ? [departments.find((d) => d.value === "all")].filter(Boolean)
        : []
    );
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setNewAnnouncementImage(null);
    setNewAnnouncementImagePreview("");
    setIsAddModalOpen(false);
  };

  const openDeleteModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedAnnouncement(null);
    setIsDeleteModalOpen(false);
  };

  const handleSelectChange = (selectedOptions = []) => {
    if (selectedOptions.some((option) => option.value === "all")) {
      setSelectedDepartment([departments.find((d) => d.value === "all")].filter(Boolean));
    } else {
      setSelectedDepartment(selectedOptions);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setNewAnnouncementImage(null);
      setNewAnnouncementImagePreview("");
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Hanya gambar PNG, JPG, JPEG dan WEBP dibenarkan", {
        autoClose: 5000,
      });
      e.target.value = "";
      return;
    }

    const maxSizeInMB = 50;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      toast.error("Saiz gambar mestilah di bawah 50MB", {
        autoClose: 5000,
      });
      e.target.value = "";
      return;
    }

    setNewAnnouncementImage(file);
    setNewAnnouncementImagePreview(URL.createObjectURL(file));
  };

  const removeAnnouncementImage = () => {
    setNewAnnouncementImage(null);
    setNewAnnouncementImagePreview("");
  };

  const handleAddAnnouncement = () => {
    if (!newAnnouncementTitle.trim() || !newAnnouncementBody.trim()) {
      toast.error("Tajuk dan kandungan tidak boleh kosong", { autoClose: 5000 });
      return;
    }

    const selectedDepartmentsPayload =
      selectedDepartment.length === 0 ||
      selectedDepartment.some((department) => department.value === "all")
        ? ["all"]
        : selectedDepartment.map((department) => department.value);

    const formData = new FormData();
    formData.append("title", newAnnouncementTitle.trim());
    formData.append("body", newAnnouncementBody.trim());

    // Keep backend compatibility: existing API expects "schools".
    // UI displays this as departments/jabatan.
    formData.append("schools", JSON.stringify(selectedDepartmentsPayload));

    if (newAnnouncementImage) {
      formData.append("image", newAnnouncementImage);
    }

    setIsSubmitting(true);

    API.post("/portal/sendBulletin", formData, {
      headers: getUploadHeaders(),
    })
      .then((response) => {
        if (response.status === 200 || response.data.response_code === 1000) {
          toast.success("Pengumuman berjaya diterbitkan", { autoClose: 5000 });
          closeAddModal();
          fetchAnnouncements();
        } else {
          toast.error(response.data.message || "Gagal menerbitkan pengumuman", {
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.log("Announcement upload error:", error?.response || error);
        toast.error("Ralat semasa menerbitkan pengumuman", { autoClose: 5000 });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDeleteAnnouncement = () => {
    if (!selectedAnnouncement) return;

    const headers = getHeaders();

    setIsSubmitting(true);

    API.post(
      "/portal/deleteBulletin",
      {
        bulletin_id: selectedAnnouncement.bulletin_id,
      },
      { headers }
    )
      .then((response) => {
        if (response.data.response_code === 1000) {
          toast.success("Pengumuman berjaya dipadam", {
            autoClose: 5000,
          });
          closeDeleteModal();
          fetchAnnouncements();
        } else {
          toast.error(response.data.message || "Gagal memadam pengumuman", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Ralat semasa memadam pengumuman", { autoClose: 5000 });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const filteredAnnouncements = useMemo(() => {
    const keyword = searchTerm.toLowerCase().trim();

    if (!keyword) return announcements;

    return announcements.filter((announcement) => {
      const title = String(announcement.title || "").toLowerCase();
      const message = String(announcement.message || "").toLowerCase();
      const departmentNames = (announcement.departments || [])
        .map((d) => d.name)
        .join(" ")
        .toLowerCase();

      return (
        title.includes(keyword) ||
        message.includes(keyword) ||
        departmentNames.includes(keyword)
      );
    });
  }, [announcements, searchTerm]);

  const totalDepartmentsReached = useMemo(() => {
    const ids = new Set();

    announcements.forEach((announcement) => {
      (announcement.departments || []).forEach((department) => {
        if (department.id) ids.add(department.id);
      });
    });

    return ids.size;
  }, [announcements]);

  const columns = [
    {
      name: "Pengumuman",
      selector: (row) => row.title,
      sortable: true,
      grow: 3,
      cell: (row) => (
        <div className="py-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className="rounded-4 d-flex align-items-center justify-content-center fw-bold"
              style={{
                width: 44,
                height: 44,
                background: THEME.sky,
                color: THEME.blue,
                flex: "0 0 auto",
              }}
            >
              P
            </div>

            <div style={{ minWidth: 0 }}>
              <div className="fw-bold text-dark mb-1">{row.title}</div>
              <div
                className="text-muted small"
                style={{
                  maxWidth: 620,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                }}
              >
                {row.message}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Sasaran Jabatan",
      selector: (row) => row.departments?.length || 0,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex flex-wrap gap-2 py-3">
          {(row.departments || []).slice(0, 3).map((department) => (
            <span
              key={`${row.bulletin_id}-${department.id}`}
              className="badge rounded-pill px-3 py-2"
              style={{
                background: THEME.sky,
                color: THEME.blue,
                fontWeight: 700,
              }}
            >
              {department.name}
            </span>
          ))}

          {(row.departments || []).length > 3 && (
            <span
              className="badge rounded-pill px-3 py-2"
              style={{
                background: "#DBEAFE",
                color: THEME.navy,
                fontWeight: 700,
              }}
            >
              +{row.departments.length - 3} lagi
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Tindakan",
      center: true,
      width: "150px",
      cell: (row) => (
        <button
          className="btn btn-sm btn-outline-danger rounded-4 fw-semibold px-3"
          onClick={() => openDeleteModal(row)}
        >
          Padam
        </button>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "#ffffff",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        minHeight: "56px",
      },
    },
    headCells: {
      style: {
        color: "#64748b",
        fontSize: "12px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      },
    },
    rows: {
      style: {
        minHeight: "92px",
        borderBottom: "1px solid #eef2f7",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#F0F7FF",
        borderBottomColor: "#BFDBFE",
        outline: "none",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #eef2f7",
        padding: "12px 16px",
      },
    },
  };

  return (
    <Fragment>
      <div
        className="container-fluid py-4"
        style={{
          background: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <div
          className="rounded-5 p-4 p-md-5 mb-4 position-relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #062E63 0%, #0B4DA2 50%, #2563EB 100%)",
            boxShadow: "0 24px 60px rgba(11, 77, 162, 0.20)",
          }}
        >
          <div
            className="position-absolute top-0 end-0 rounded-circle"
            style={{
              width: 260,
              height: 260,
              background: "rgba(255,255,255,0.12)",
              transform: "translate(30%, -35%)",
            }}
          />

          <div
            className="position-absolute bottom-0 start-50 rounded-circle"
            style={{
              width: 180,
              height: 180,
              background: "rgba(255,255,255,0.08)",
              transform: "translate(-50%, 45%)",
            }}
          />

          <div className="position-relative d-flex flex-column flex-lg-row justify-content-between gap-4">
            <div>
              <div
                className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-3"
                style={{
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.24)",
                  backdropFilter: "blur(10px)",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <span
                  className="rounded-circle"
                  style={{
                    width: 8,
                    height: 8,
                    background: "#fff",
                    display: "inline-block",
                  }}
                />
                Komunikasi Jabatan
              </div>

              <h1
                className="text-white fw-black mb-2"
                style={{
                  fontSize: "clamp(32px, 4vw, 52px)",
                  letterSpacing: "-1.5px",
                }}
              >
                Pengumuman
              </h1>

              <p
                className="text-white-50 mb-0"
                style={{
                  maxWidth: 720,
                  fontSize: 16,
                }}
              >
                Cipta, urus dan hantar pengumuman kepada semua jabatan atau jabatan tertentu.
              </p>
            </div>

            <div className="d-flex align-items-start gap-2">
              <button
                type="button"
                className="btn border-0 rounded-4 fw-bold px-4 py-3"
                style={{
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.24)",
                }}
                onClick={fetchAnnouncements}
                disabled={isLoading}
              >
                Muat Semula
              </button>

              <button
                type="button"
                className="btn border-0 rounded-4 fw-bold px-4 py-3"
                style={{
                  background: "#ffffff",
                  color: THEME.blue,
                  boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
                }}
                onClick={openAddModal}
              >
                Tambah Pengumuman
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">
                  Jumlah Pengumuman
                </div>
                <div className="display-6 fw-black text-dark">
                  {announcements.length}
                </div>
                <div className="text-muted small mt-2">
                  Mesej unik yang telah dicipta
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">
                  Jabatan Terlibat
                </div>
                <div className="display-6 fw-black" style={{ color: THEME.blue }}>
                  {totalDepartmentsReached}
                </div>
                <div className="text-muted small mt-2">
                  Jabatan yang menjadi sasaran
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">
                  Hasil Carian
                </div>
                <div className="display-6 fw-black text-primary">
                  {filteredAnnouncements.length}
                </div>
                <div className="text-muted small mt-2">
                  Pengumuman yang sepadan
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm rounded-5 mb-4">
          <div className="card-body p-4">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <h6 className="fw-black text-dark mb-1">
                  Senarai Pengumuman
                </h6>
                <div className="text-muted small">
                  Cari berdasarkan tajuk, kandungan mesej atau nama jabatan.
                </div>
              </div>

              <div className="d-flex flex-column flex-md-row gap-2">
                <input
                  className="form-control rounded-4"
                  style={{ minWidth: 320 }}
                  placeholder="Cari pengumuman..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-light rounded-4 fw-semibold px-4"
                    onClick={() => setSearchTerm("")}
                  >
                    Kosongkan
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm rounded-5 overflow-hidden">
          <div className="card-header bg-white border-0 p-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div>
                <h5 className="fw-black text-dark mb-1">
                  Pengumuman Diterbitkan
                </h5>
                <div className="text-muted small">
                  Memaparkan {filteredAnnouncements.length} daripada {announcements.length}{" "}
                  rekod pengumuman.
                </div>
              </div>

              <div
                className="rounded-pill px-3 py-2 small fw-semibold"
                style={{
                  background: THEME.sky,
                  color: THEME.blue,
                }}
              >
                Paparan Admin
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredAnnouncements}
            progressPending={isLoading}
            persistTableHead
            pagination
            highlightOnHover
            customStyles={customStyles}
            noDataComponent={
              <div className="text-center py-5">
                <div
                  className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 70,
                    height: 70,
                    background: THEME.sky,
                    color: THEME.blue,
                    fontWeight: 900,
                    fontSize: 24,
                  }}
                >
                  P
                </div>

                <div className="fw-bold text-dark mb-1">
                  Tiada pengumuman dijumpai
                </div>

                <div className="text-muted small">
                  Cipta pengumuman baharu atau ubah kata kunci carian.
                </div>
              </div>
            }
          />
        </div>
      </div>

      {/* Add Announcement Modal */}
      <Modal
        isOpen={isAddModalOpen}
        toggle={closeAddModal}
        centered
        size="lg"
        className="modal-dialog-scrollable"
      >
        <ModalHeader toggle={closeAddModal}>
          <div>
            <div className="text-muted small fw-semibold">
              Mesej Baharu
            </div>
            <div className="fw-black text-dark">Tambah Pengumuman</div>
          </div>
        </ModalHeader>

        <ModalBody style={{ background: "#f8fafc" }}>
          <div className="card border-0 rounded-5 shadow-sm">
            <div className="card-body p-4">
              <div className="mb-3">
                <label className="form-label fw-semibold small text-muted">
                  Tajuk
                </label>
                <input
                  type="text"
                  className="form-control form-control-lg rounded-4"
                  placeholder="Contoh: Notis Operasi Jabatan"
                  value={newAnnouncementTitle}
                  onChange={(e) => setNewAnnouncementTitle(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small text-muted">
                  Kandungan
                </label>
                <textarea
                  className="form-control rounded-4"
                  rows="6"
                  placeholder="Tulis kandungan pengumuman..."
                  value={newAnnouncementBody}
                  onChange={(e) => setNewAnnouncementBody(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small text-muted">
                  Gambar
                </label>

                <div
                  className="rounded-5 p-4"
                  style={{
                    background: "#fff",
                    border: "1px dashed #BFDBFE",
                  }}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="form-control rounded-4"
                    onChange={handleImageChange}
                  />

                  <div className="text-muted small mt-2">
                    Pilihan. Muat naik PNG, JPG, JPEG atau WEBP. Maksimum 50MB.
                  </div>

                  {newAnnouncementImagePreview && (
                    <div className="mt-3">
                      <img
                        src={newAnnouncementImagePreview}
                        alt="Pratonton pengumuman"
                        style={{
                          width: "100%",
                          maxHeight: 260,
                          objectFit: "cover",
                          borderRadius: 18,
                          border: "1px solid #BFDBFE",
                        }}
                      />

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-4 fw-semibold mt-3"
                        onClick={removeAnnouncementImage}
                      >
                        Buang Gambar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label fw-semibold small text-muted">
                  Jabatan Sasaran
                </label>

                <Select
                  isMulti
                  options={departments}
                  value={selectedDepartment}
                  onChange={handleSelectChange}
                  placeholder="Pilih jabatan..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: 16,
                      borderColor: "#e2e8f0",
                      minHeight: 48,
                      boxShadow: "none",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      borderRadius: 999,
                      background: THEME.sky,
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: THEME.blue,
                      fontWeight: 700,
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: THEME.blue,
                      ":hover": {
                        background: "#BFDBFE",
                        color: THEME.navy,
                      },
                    }),
                  }}
                />

                <div className="text-muted small mt-2">
                  Pilih Semua Jabatan untuk menghantar pengumuman kepada semua jabatan tersedia.
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            className="rounded-4 fw-semibold px-4"
            onClick={closeAddModal}
            disabled={isSubmitting}
          >
            Batal
          </Button>

          <Button
            color="primary"
            className="rounded-4 fw-bold px-4"
            onClick={handleAddAnnouncement}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sedang Menerbitkan..." : "Terbitkan Pengumuman"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Announcement Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal} centered>
        <ModalHeader toggle={closeDeleteModal}>
          <div>
            <div className="text-muted small fw-semibold">
              Pengesahan Padam
            </div>
            <div className="fw-black text-dark">Padam Pengumuman</div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div
            className="rounded-5 p-4"
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
            }}
          >
            <div className="fw-bold text-dark mb-2">
              Adakah anda pasti mahu memadam pengumuman ini?
            </div>

            <div className="text-muted small mb-3">
              Tindakan ini tidak boleh dibatalkan.
            </div>

            <div className="fw-semibold text-danger">
              {selectedAnnouncement?.title}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color="light"
            className="rounded-4 fw-semibold px-4"
            onClick={closeDeleteModal}
            disabled={isSubmitting}
          >
            Batal
          </Button>

          <Button
            color="danger"
            className="rounded-4 fw-bold px-4"
            onClick={handleDeleteAnnouncement}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sedang Memadam..." : "Ya, Padam"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AnnouncementPage;